import { computed, signal } from '@preact/signals-core';
import type { ReadonlySignal } from '@preact/signals-core';
import type { GridColumnWidthChange } from '@thinkalpha/ui-table/interactions/columnResize';
import type { GridColumnsInput, GridColumnPin } from '@thinkalpha/ui-table/types/column';
import type { TimeListColumnPreference } from '@thinkalpha/ui-types/contracts/workspace';

export interface TimeListColumnState {
    preferences: ReadonlySignal<readonly TimeListColumnPreference[]>;
    update: (columns: TimeListColumnPreference[]) => void;
}

export class TimeListColumns implements Disposable {
    #disposed = false;
    readonly #defaults: GridColumnsInput;
    readonly #preferences: ReadonlySignal<readonly TimeListColumnPreference[]>;
    readonly #persist: (columns: TimeListColumnPreference[]) => void;
    readonly columns;

    constructor(defaults: GridColumnsInput, owner?: TimeListColumnState) {
        this.#defaults = defaults;
        if (owner) {
            this.#preferences = owner.preferences;
            this.#persist = owner.update;
        } else {
            const preferences = signal<readonly TimeListColumnPreference[]>([]);
            this.#preferences = preferences;
            this.#persist = (columns) => {
                preferences.value = columns;
            };
        }
        this.columns = computed(() => ({
            ...this.#defaults,
            columnState: this.#defaults.columnState.map((column) => {
                const saved = this.#preferences.value.find((p) => p.columnId === column.columnId);
                return saved
                    ? {
                          ...column,
                          order: Number.isFinite(saved.order) ? saved.order : column.order,
                          visible: saved.visible,
                          pinned: saved.pinned,
                          width:
                              saved.width !== undefined && Number.isFinite(saved.width) && saved.width >= 40
                                  ? saved.width
                                  : column.width,
                      }
                    : column;
            }),
        }));
    }

    #commit(columns: TimeListColumnPreference[]): void {
        if (this.#disposed) return;
        this.#persist(columns);
    }
    readonly resize = ({ columnId, width }: GridColumnWidthChange): void => {
        this.#commit(this.columns.peek().columnState.map((c) => (c.columnId === columnId ? { ...c, width } : c)));
    };
    readonly reorder = (ids: readonly string[]): void => {
        this.#commit(
            this.columns.peek().columnState.map((c) => ({
                ...c,
                order: ids.includes(c.columnId) ? ids.indexOf(c.columnId) : c.order,
            })),
        );
    };
    pin(columnId: string, pinned?: GridColumnPin): void {
        this.#commit(this.columns.peek().columnState.map((c) => (c.columnId === columnId ? { ...c, pinned } : c)));
    }
    reset(): void {
        this.#commit([]);
    }
    [Symbol.dispose](): void {
        this.#disposed = true;
    }
}
